import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { useGetBooksQuery, BookSummary } from "../../services/rtkApi/agentApi";
import { useIsWriter } from "./useIsWriter";

/**
 * Public list of books at `/blog/books`. A book is a topic that gathers
 * chapters — ordinary posts — released one at a time, so the useful thing to
 * show here is how far along each one is.
 */
const BooksPage: React.FC = () => {
  const { data, isLoading, isError } = useGetBooksQuery();
  const { isWriter: canWrite } = useIsWriter();

  return (
    <PageContainer>
      <Inner>
        <PageHeader>
          <Title>books</Title>
          <Subtitle>longer things, a chapter at a time</Subtitle>
          <HeaderNav>
            <NavLink to="/blog">← the blog</NavLink>
            {canWrite && <NavLink to="/blog/manage/books">manage books</NavLink>}
          </HeaderNav>
        </PageHeader>

        {isLoading && <Muted>loading books…</Muted>}
        {isError && <Muted>could not load the books</Muted>}
        {!isLoading && !isError && data?.books.length === 0 && (
          <EmptyState>no books yet. the first one is being written.</EmptyState>
        )}

        <Grid>
          {data?.books.map((book) => (
            <BookCard key={book._id} book={book} />
          ))}
        </Grid>
      </Inner>
    </PageContainer>
  );
};

const BookCard: React.FC<{ book: BookSummary }> = ({ book }) => (
  <CardLink to={`/blog/books/${book.slug}`}>
    <Card>
      {book.coverImageUrl && <Cover src={book.coverImageUrl} alt="" loading="lazy" />}
      <CardBody>
        <CardMeta>
          <KindTag>book</KindTag>
          {book.complete ? <CompleteTag>complete</CompleteTag> : <OngoingTag>ongoing</OngoingTag>}
          <ChapterCount>
            {book.chapterCount} {book.chapterCount === 1 ? "chapter" : "chapters"}
          </ChapterCount>
        </CardMeta>
        <CardTitle>{book.title}</CardTitle>
        {book.subtitle && <CardSubtitle>{book.subtitle}</CardSubtitle>}
        {!book.subtitle && book.excerpt && <CardSubtitle>{book.excerpt}</CardSubtitle>}
        {book.authorUsername && <ByLine>by @{book.authorUsername}</ByLine>}
        {book.tags.length > 0 && (
          <TagList>
            {book.tags.map((tag) => (
              <Tag key={tag}>{tag}</Tag>
            ))}
          </TagList>
        )}
      </CardBody>
    </Card>
  </CardLink>
);

const PageContainer = styled.div`
  min-height: 100vh;
  background-color: #f0f0f0;
  padding: 20px;

  @media (max-width: 700px) {
    padding: 12px;
  }
`;

const Inner = styled.div`
  max-width: 880px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 28px;
  padding: 20px 0;
`;

const Title = styled.h1`
  font-size: 3.5rem;
  color: #222;
  text-transform: uppercase;
  margin: 0;
  text-shadow: 2px 2px 5px #3c3c3cae;

  @media (max-width: 700px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: #666;
  margin: 8px 0 0;
  font-style: italic;
`;

const HeaderNav = styled.div`
  display: flex;
  justify-content: center;
  gap: 18px;
  margin-top: 14px;
`;

const NavLink = styled(Link)`
  color: #466a5d;
  font-size: 0.9rem;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 50px;
`;

const CardLink = styled(Link)`
  text-decoration: none;
  color: inherit;
`;

const Card = styled.div`
  display: flex;
  gap: 18px;
  background: white;
  border: 1px solid #ccc;
  box-shadow: 2px 3px rgba(0, 0, 0, 0.15);
  transition: all 0.15s;

  &:hover {
    box-shadow: 3px 5px rgba(0, 0, 0, 0.25);
    transform: translateY(-1px);
  }

  @media (max-width: 620px) {
    flex-direction: column;
    gap: 0;
  }
`;

const Cover = styled.img`
  width: 150px;
  object-fit: cover;
  border-right: 1px solid #eee;

  @media (max-width: 620px) {
    width: 100%;
    max-height: 180px;
    border-right: none;
    border-bottom: 1px solid #eee;
  }
`;

const CardBody = styled.div`
  padding: 18px 22px;
  flex: 1;
  min-width: 0;
`;

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 8px;
`;

const KindTag = styled.span`
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6a4d8a;
  background: #f3edfa;
  padding: 2px 8px;
  border: 1px solid #d9c9ec;
`;

const CompleteTag = styled(KindTag)`
  color: #466a5d;
  background: #d4f9eb;
  border-color: #466a5d;
`;

const OngoingTag = styled(KindTag)`
  color: #b58b2a;
  background: #fdf1d3;
  border-color: #e0c37a;
`;

const ChapterCount = styled.span`
  font-size: 0.8rem;
  color: #888;
`;

const CardTitle = styled.h2`
  font-size: 1.4rem;
  color: #222;
  margin: 0 0 4px;
  line-height: 1.3;
`;

const CardSubtitle = styled.p`
  font-size: 0.95rem;
  color: #666;
  margin: 0 0 6px;
  line-height: 1.4;
`;

const ByLine = styled.span`
  font-size: 0.8rem;
  color: #999;
  font-style: italic;
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
`;

const Tag = styled.span`
  font-size: 0.7rem;
  color: #777;
  background: #f5f5f5;
  padding: 2px 8px;
  border: 1px solid #ddd;
`;

const Muted = styled.p`
  text-align: center;
  color: #888;
  padding: 40px 0;
`;

const EmptyState = styled.p`
  text-align: center;
  color: #888;
  font-style: italic;
  padding: 60px 0;
`;

export default BooksPage;
